/**
 * Logger utility with configurable log levels
 * Supports: debug, info, warning, error
 *
 * Default behavior:
 * - Development: debug level (all logs shown)
 * - Production: warning level (warning and error only)
 *
 * Override via localStorage:
 * localStorage.setItem('LOG_LEVEL', 'debug|info|warning|error')
 */

type LogLevel = "debug" | "info" | "warning" | "error";

const LogLevelPriority: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warning: 2,
  error: 3,
};

const getLogLevel = (): LogLevel => {
  // Check localStorage override first
  const storedLevel = localStorage.getItem("LOG_LEVEL");
  if (storedLevel && LogLevelPriority.hasOwnProperty(storedLevel)) {
    return storedLevel as LogLevel;
  }

  // Default based on environment
  return process.env.NODE_ENV === "development" ? "debug" : "warning";
};

const shouldLog = (level: LogLevel): boolean => {
  const currentLevel = getLogLevel();
  return LogLevelPriority[level] >= LogLevelPriority[currentLevel];
};

export const logger = {
  debug: (message: string, ...args: any[]): void => {
    if (shouldLog("debug")) {
      console.log(`[DEBUG] ${message}`, ...args);
    }
  },
  info: (message: string, ...args: any[]): void => {
    if (shouldLog("info")) {
      console.info(`[INFO] ${message}`, ...args);
    }
  },
  warn: (message: string, ...args: any[]): void => {
    if (shouldLog("warning")) {
      console.warn(`[WARN] ${message}`, ...args);
    }
  },
  warning: (message: string, ...args: any[]): void => {
    if (shouldLog("warning")) {
      console.warn(`[WARN] ${message}`, ...args);
    }
  },
  error: (message: string, ...args: any[]): void => {
    if (shouldLog("error")) {
      console.error(`[ERROR] ${message}`, ...args);
    }
  },
};

export default logger;
